// B2B wholesale sales screen at /wholesale.
// Creates draft orders with orderType="wholesale" sourced from the wholesale
// workstation's warehouse (Kho Bán Buôn). Attaching a wholesale customer
// automatically keeps server-side wholesale pricing.
//
// Warehouse sourcing: admin picks the workstation via the WorkstationSelector
// dropdown. The workstation ID is stored in localStorage under
// "wholesale_workstation_id". createDraft(workstationId, "wholesale") passes
// this ID so the backend resolveOrderWarehouse() picks the correct warehouse.

import { useState, useCallback, useEffect, useRef } from "react";
import { Search, Package2, ScanLine, WarehouseIcon } from "lucide-react";
import { usePosCart, type PaymentPart } from "@/features/pos/use-pos-cart";
import { CustomerSelect } from "@/features/pos/customer-select";
import { ProductSearchModal } from "@/features/pos/product-search-modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiGet } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { toast } from "@/components/ui/toast";
import { useScanResolve, type ScanContext, type ScanSerialCandidate } from "@/features/inventory/use-scan-resolve";
import { useScannerAutoSubmit } from "@/features/inventory/use-scanner-auto-submit";
import { SerialPickModal } from "@/features/inventory/serial-pick-modal";
import { useAuthStore } from "@/lib/auth/store";
import { WholesaleLineTable } from "./wholesale-line-table";
import { WholesalePaymentPanel } from "./wholesale-payment-panel";
import { useWarehouses, initialWarehouseId, warehouseStorageKey } from "@/features/inventory/use-warehouses";

interface VariantResult {
  id: string;
  sku: string;
  productName: string;
  imageUrls?: string[];
}

// Derive warehouse ID from the committed order by fetching it once.
// The backend assigns warehouse via the workstation; we surface it here
// so the serial selector can filter by warehouse.
function useOrderWarehouseId(orderId: string | undefined) {
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  useEffect(() => {
    if (!orderId) { setWarehouseId(null); return; }
    // The order API returns warehouseId in the order object if available.
    // Fetch order detail to get it.
    apiGet<{ warehouseId?: string | null }>(`/v1/orders/${orderId}`)
      .then((o) => setWarehouseId(o.warehouseId ?? null))
      .catch(() => {/* non-critical — serials will show without warehouse filter */});
  }, [orderId]);
  return warehouseId;
}

export function WholesalePage() {
  const {
    order,
    addItem,
    updateItem,
    removeItem,
    patchHeader,
    commit,
    createDraft,
    clearCart,
    warehouseId,
    setWarehouseId,
  } = usePosCart();

  // Quyền sửa đơn giá: admin luôn có, còn lại theo cờ can_edit_price.
  const canEditPrice = useAuthStore((s) => s.user?.role === "admin" || !!s.user?.canEditPrice);

  // Warehouse list (from the kho module) + default the wholesale kho once loaded.
  const { data: warehouses = [] } = useWarehouses();
  useEffect(() => {
    if (warehouseId || warehouses.length === 0) return;
    const init = initialWarehouseId(warehouses, "wholesale");
    if (init) setWarehouseId(init);
  }, [warehouses, warehouseId, setWarehouseId]);
  const onWarehouseChange = useCallback(
    (id: string) => {
      setWarehouseId(id);
      try {
        localStorage.setItem(warehouseStorageKey("wholesale"), id);
      } catch {
        /* ignore storage errors */
      }
    },
    [setWarehouseId],
  );

  const [searchOpen, setSearchOpen] = useState(false);
  const [barcodeValue, setBarcodeValue] = useState("");
  const [busy, setBusy] = useState(false);
  const barcodeRef = useRef<HTMLInputElement>(null);

  // Serial-first IMEI scan state
  const [imeiValue, setImeiValue] = useState("");
  // Alias 6 số trúng nhiều máy → modal chọn thủ công.
  const [serialChoices, setSerialChoices] = useState<{ q: string; list: ScanSerialCandidate[] } | null>(null);
  const [imeiScanning, setImeiScanning] = useState(false);
  const imeiRef = useRef<HTMLInputElement>(null);

  // Warehouse resolved on the committed order — used by the line table to filter
  // sellable serials to the order's kho.
  const orderWarehouseId = useOrderWarehouseId(order?.id);

  // Build scan context: "sell" with the active draft's order ID.
  // Falls back to a disabled-placeholder when no draft exists; the scan box
  // is gated on order?.id so resolve() is never called in that state.
  const imeiScanCtx: ScanContext = order?.id
    ? { context: "sell", orderId: order.id }
    : { context: "sell", orderId: "" };
  const { resolve: resolveImei } = useScanResolve(imeiScanCtx);

  // On mount or when the selling warehouse becomes available: ensure a wholesale
  // draft exists, bound to the chosen kho.
  const draftInitiated = useRef(false);
  useEffect(() => {
    if (!warehouseId || draftInitiated.current || order) return;
    draftInitiated.current = true;
    createDraft(warehouseId, "wholesale").catch((err: unknown) => {
      toast.error(translateApiError(err));
      draftInitiated.current = false;
    });
  }, [warehouseId, order, createDraft]);

  // Keep focus on barcode box after mutations so adding many lines is fast.
  useEffect(() => {
    if (!addItem.isPending) {
      const t = setTimeout(() => barcodeRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [addItem.isPending]);

  const submitBarcode = useCallback(async () => {
    if (!barcodeValue.trim() || busy) return;
    setBusy(true);
    try {
      const results = await apiGet<VariantResult[]>("/v1/variants/search", {
        q: barcodeValue.trim(),
        limit: 5,
      });
      setBarcodeValue("");
      if (!results || results.length === 0) {
        toast.error("Không tìm thấy sản phẩm");
        return;
      }
      if (results.length === 1) {
        addItem.mutate(
          { variantId: results[0].id },
          { onError: (err) => toast.error(translateApiError(err)) },
        );
      } else {
        setSearchOpen(true);
      }
    } catch (err) {
      toast.error(translateApiError(err));
    } finally {
      setBusy(false);
      barcodeRef.current?.focus();
    }
  }, [barcodeValue, busy, addItem]);

  const handleBarcodeKey = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") { e.preventDefault(); void submitBarcode(); }
  }, [submitBarcode]);

  const handleSelectVariant = useCallback((variantId: string) => {
    addItem.mutate(
      { variantId },
      { onError: (err) => toast.error(translateApiError(err)) },
    );
    setSearchOpen(false);
    setTimeout(() => barcodeRef.current?.focus(), 80);
  }, [addItem]);

  const handleSelectCustomer = useCallback((c: { id: string } | null) => {
    patchHeader.mutate(
      { customerId: c?.id ?? undefined },
      { onError: (err) => toast.error(translateApiError(err)) },
    );
  }, [patchHeader]);

  const handleUpdateQty = useCallback((itemId: string, qty: number) => {
    updateItem.mutate(
      { itemId, quantity: qty },
      { onError: (err) => toast.error(translateApiError(err)) },
    );
  }, [updateItem]);

  const handleUpdatePrice = useCallback((itemId: string, price: string) => {
    updateItem.mutate(
      { itemId, unitPrice: price },
      { onError: (err) => toast.error(translateApiError(err)) },
    );
  }, [updateItem]);

  const handleUpdateDiscount = useCallback((itemId: string, discount: string) => {
    updateItem.mutate(
      { itemId, discount },
      { onError: (err) => toast.error(translateApiError(err)) },
    );
  }, [updateItem]);

  const handleUpdateSerials = useCallback((itemId: string, serialIds: string[]) => {
    updateItem.mutate(
      { itemId, serialIds },
      {
        onError: (err) => {
          // Surface backend serial validation errors clearly (wrong variant / already sold / wrong warehouse)
          toast.error(translateApiError(err));
        },
      },
    );
  }, [updateItem]);

  const handleRemove = useCallback((itemId: string) => {
    removeItem.mutate(itemId, {
      onError: (err) => toast.error(translateApiError(err)),
    });
  }, [removeItem]);

  const handleCommit = useCallback(({ paymentMethod, tenderedAmount, payments, reason }: {
    paymentMethod: string;
    tenderedAmount: number;
    payments?: PaymentPart[];
    reason?: string;
  }) => {
    commit.mutate(
      { paymentMethod, tenderedAmount, payments, reason },
      {
        onError: (err) => toast.error(translateApiError(err)),
      },
    );
  }, [commit]);

  const handleNewOrder = useCallback(() => {
    clearCart();
    draftInitiated.current = false;
    setBarcodeValue("");
    setImeiValue("");
    // A fresh draft is created by the effect above once a warehouse is selected.
  }, [clearCart]);

  // Switching kho starts a fresh wholesale draft in the newly chosen warehouse.
  const handleWarehouseChange = useCallback(
    (id: string) => {
      onWarehouseChange(id);
      clearCart();
      draftInitiated.current = false;
      setBarcodeValue("");
      setImeiValue("");
    },
    [onWarehouseChange, clearCart],
  );

  // Gắn 1 serial đã resolve vào đơn: tìm/tạo dòng theo variant + merge đủ bộ
  // serial (updateItem REPLACES the full serial set).
  const attachResolvedSerial = useCallback(
    (variantId: string, serialId: string) => {
      if (!order?.id) return;
      const currentItems = order.items ?? [];
      const existingItem = currentItems.find((it) => it.variantId === variantId);

      if (existingItem) {
        // Line already exists — build full merged serial set and replace
        const existingIds = (existingItem.serials ?? []).map((s) => s.id);

        if (existingIds.includes(serialId)) {
          toast.info("Serial đã có trong đơn hàng");
          return;
        }

        const mergedIds = [...existingIds, serialId];
        updateItem.mutate(
          { itemId: existingItem.id, serialIds: mergedIds, quantity: mergedIds.length },
          { onError: (err) => toast.error(translateApiError(err)) },
        );
      } else {
        // No line yet — addItem first, then attach the serial via updateItem.
        // addItem returns the full updated order; we find the newly created item
        // from the response to get its ID for the subsequent updateItem call.
        addItem.mutate(
          { variantId },
          {
            onSuccess: (updatedOrder) => {
              const newItem = (updatedOrder.items ?? []).find(
                (it) => it.variantId === variantId,
              );
              if (!newItem) {
                toast.error("Không thể gắn serial: dòng vừa tạo không tìm thấy");
                return;
              }
              // New item has no serials yet; send the single serial as the full set
              updateItem.mutate(
                { itemId: newItem.id, serialIds: [serialId], quantity: 1 },
                { onError: (err) => toast.error(translateApiError(err)) },
              );
            },
            onError: (err) => toast.error(translateApiError(err)),
          },
        );
      }
    },
    [order, addItem, updateItem],
  );

  // Serial-first scan: quét IMEI → resolve → tự tìm/tạo dòng + gắn serial.
  const submitImei = useCallback(
    async () => {
      if (!imeiValue.trim() || imeiScanning || !order?.id) return;
      const code = imeiValue.trim();

      setImeiScanning(true);
      try {
        const sr = await resolveImei(code);
        setImeiValue("");

        if (sr.kind === "none") {
          toast.error("Không tìm thấy serial hoặc sản phẩm");
          return;
        }

        if (sr.kind === "variant") {
          // Fuzzy variant match — delegate to existing barcode flow
          if (sr.matches.length === 1) {
            addItem.mutate(
              { variantId: sr.matches[0].id },
              { onError: (err) => toast.error(translateApiError(err)) },
            );
          } else {
            setSearchOpen(true);
          }
          return;
        }

        // Nhiều máy còn tồn trùng đuôi số → nhân viên tự chọn.
        if (sr.kind === "serials") {
          setSerialChoices({ q: code, list: sr.serials });
          return;
        }

        // kind === "serial"
        if (!sr.ok) {
          toast.error(sr.reason ?? "Serial không hợp lệ cho kho bán buôn");
          return;
        }

        attachResolvedSerial(sr.variant.id, sr.serial.id);
      } finally {
        setImeiScanning(false);
        setTimeout(() => imeiRef.current?.focus(), 60);
      }
    },
    [imeiValue, imeiScanning, order, resolveImei, addItem, attachResolvedSerial],
  );

  // Máy quét không gửi Enter → tự chốt khi vừa tít xong (Enter vẫn dùng được).
  useScannerAutoSubmit(imeiValue, () => void submitImei(), {
    enabled: !!order?.id && !imeiScanning,
  });

  const handleImeiKey = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") { e.preventDefault(); void submitImei(); }
  }, [submitImei]);

  const mutationLoading =
    addItem.isPending || updateItem.isPending || removeItem.isPending;

  const items = order?.items ?? [];

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center gap-3 border-b bg-card px-4 py-2 shrink-0 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Package2 size={18} className="text-primary shrink-0" />
          <span className="font-semibold text-sm">Bán sỉ</span>
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            BÁN SỈ
          </span>
          {order && (
            <span className="text-xs text-muted-foreground font-mono">{order.code}</span>
          )}
        </div>

        {/* Warehouse selector — kho xuất hàng (mặc định Kho Bán Buôn) */}
        <div className="flex items-center gap-1.5">
          <WarehouseIcon size={14} className="shrink-0 text-muted-foreground" />
          <select
            value={warehouseId ?? ""}
            onChange={(e) => handleWarehouseChange(e.target.value)}
            title="Chọn kho xuất hàng cho đơn sỉ"
            className="rounded-md border border-input bg-background px-2 py-1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {warehouses.length === 0 && <option value="">Đang tải…</option>}
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>

        {/* Customer selector */}
        <CustomerSelect
          selectedId={order?.customerId}
          onSelect={handleSelectCustomer}
          mode="chip"
        />

        {/* Barcode / SKU quick-add input — stays focused for bulk entry */}
        <div className="flex flex-1 min-w-[200px] max-w-sm gap-1">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              ref={barcodeRef}
              value={barcodeValue}
              onChange={(e) => setBarcodeValue(e.target.value)}
              onKeyDown={handleBarcodeKey}
              enterKeyHint="search"
              placeholder="Barcode / SKU…"
              disabled={!warehouseId || busy || addItem.isPending}
              className="pl-8 font-mono text-sm h-9"
              autoComplete="off"
            />
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => void submitBarcode()}
            disabled={!warehouseId || busy || addItem.isPending || !barcodeValue.trim()}
            className="h-9 shrink-0"
          >
            Thêm
          </Button>
        </div>

        {/* IMEI / serial scan box — serial-first: quét IMEI → tự thêm dòng + gắn serial */}
        <div className="flex flex-1 min-w-[200px] max-w-sm gap-1">
          <div className="relative flex-1">
            <ScanLine size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              ref={imeiRef}
              value={imeiValue}
              onChange={(e) => setImeiValue(e.target.value)}
              onKeyDown={handleImeiKey}
              enterKeyHint="done"
              placeholder={
                order?.id
                  ? "Quét / gõ IMEI Serial…"
                  : "Cần có đơn hàng để quét serial"
              }
              disabled={!order?.id || imeiScanning || addItem.isPending || updateItem.isPending}
              className="pl-8 font-mono text-sm h-9"
              autoComplete="off"
            />
            {imeiScanning && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                …
              </span>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => void submitImei()}
            disabled={!order?.id || imeiScanning || addItem.isPending || updateItem.isPending || !imeiValue.trim()}
            className="h-9 shrink-0"
          >
            Thêm
          </Button>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setSearchOpen(true)}
          disabled={!warehouseId}
          className="gap-1.5 shrink-0"
        >
          <Search size={14} />
          Tìm sản phẩm
        </Button>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: line table */}
        <div className="flex flex-1 flex-col overflow-auto">
          <WholesaleLineTable
            items={items}
            warehouseId={orderWarehouseId}
            loading={mutationLoading}
            canEditPrice={canEditPrice}
            onUpdateQty={handleUpdateQty}
            onUpdatePrice={handleUpdatePrice}
            onUpdateDiscount={handleUpdateDiscount}
            onUpdateSerials={handleUpdateSerials}
            onRemove={handleRemove}
          />
        </div>

        {/* Right: payment panel */}
        <div className="hidden lg:flex w-[320px] shrink-0 border-l bg-card flex-col overflow-y-auto">
          <WholesalePaymentPanel
            order={order}
            workstationId={null}
            loading={commit.isPending}
            onCommit={handleCommit}
            onNewOrder={handleNewOrder}
          />
        </div>
      </div>

      {/* Mobile payment button — visible on small screens */}
      <div className="lg:hidden border-t bg-card px-4 py-2 flex items-center justify-between shrink-0">
        <div className="text-sm">
          {order?.total ? (
            <span className="font-semibold font-mono">
              {new Intl.NumberFormat("vi-VN").format(parseFloat(order.total))} ₫
            </span>
          ) : (
            <span className="text-muted-foreground">Chưa có đơn</span>
          )}
        </div>
        {order?.status === "paid" || order?.status === "partial_paid" ? (
          <Button size="sm" onClick={handleNewOrder}>Đơn mới</Button>
        ) : (
          <Button
            size="sm"
            disabled={!order?.items?.length || commit.isPending}
            onClick={() => {
              // On mobile, the full panel scrolls into view; minimal UX here.
              toast.info("Kéo qua phải để thanh toán trên màn hình lớn hơn.");
            }}
          >
            Thanh toán
          </Button>
        )}
      </div>

      <ProductSearchModal
        open={searchOpen}
        onSelect={handleSelectVariant}
        onClose={() => setSearchOpen(false)}
      />

      {serialChoices && (
        <SerialPickModal
          candidates={serialChoices.list}
          query={serialChoices.q}
          onPick={(c) => {
            setSerialChoices(null);
            attachResolvedSerial(c.variant.id, c.serial.id);
            setTimeout(() => imeiRef.current?.focus(), 60);
          }}
          onClose={() => {
            setSerialChoices(null);
            setTimeout(() => imeiRef.current?.focus(), 60);
          }}
        />
      )}
    </div>
  );
}
